import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Tooltip from '@mui/material/Tooltip';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import IconButton from '@mui/material/IconButton';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';
import { useGridApiContext } from '@mui/x-data-grid-premium';
import { getEntityPrefix, getOldBlueColor } from '../../../../utils/utilFunctions';
import { getLocalAuth } from '../../../../utils/environmentUtils';
import { setFormMetaInfo } from '../../../../components/FormBuilderLibrary/slice';
import { useUpdateArchiveStatusStageMutation } from '../../../../services/endpoints/formBuilderEndpoints';
import {
	usePostCopyTemplateMutation,
	usePostUnlinkTemplateMutation
} from '../../../../services/endpoints/templatesEndpoints';
import TableTitle from '../../../../components/TableTitle/TableTitle';
import BasicCellText from './BasicCellText';
import ActionButton from './ActionButton';

const StageTemplateColumns = (columnWidths, handleAlert) => {
	const accountSource = useSelector((state) => state?.login?.accountSource);
	const apiToken = getLocalAuth(accountSource)?.access_token;
	const [openTooltip, setOpenTooltip] = useState(0);
	const [searchParams, setSearchParams] = useSearchParams();

	const [updateArchiveStatus] = useUpdateArchiveStatusStageMutation();
	const [unlinkTemplate] = usePostUnlinkTemplateMutation();
	const [copyTemplate] = usePostCopyTemplateMutation();
	const dispatch = useDispatch();

	const handleCopy = (element, type) => {
		let unlinkTemplateOptions = {
			apiToken: apiToken,
			payload: {
				element: element,
				type: type
			}
		};
		copyTemplate(unlinkTemplateOptions)
			.unwrap()
			.then(async () => {
				handleAlert('Template Copied Successfully!', 'success');
			})
			.catch(() => {
				handleAlert('Template Copying Failed!', 'error');
			});
	};

	const handleUnlinking = (parent, child, type) => {
		let unlinkTemplateOptions = {
			apiToken: apiToken,
			payload: {
				parent: parent,
				child: child,
				type: type
			}
		};
		unlinkTemplate(unlinkTemplateOptions)
			.unwrap()
			.then(async () => {
				handleAlert('Template Unlinked Successfully!', 'success');
			})
			.catch(() => {
				handleAlert('Template Unlinking Failed!', 'error');
			});
	};

	const handleArchiveUpdate = (id, archive_status) => {
		let updateFormOptions = {
			stage_id: id,
			apiToken: apiToken,
			payload: { is_archive: archive_status, is_template: true }
		};
		updateArchiveStatus(updateFormOptions)
			.unwrap()
			.then(async () => {
				handleAlert('Archive Status Changed Successfully!', 'success');
			})
			.catch(() => {
				handleAlert('Archive Status Change Failed!', 'error');
			});
	};

	return [
		{
			field: 'title',
			description: 'Template Title',

			hideDescendantCount: true,
			headerName: 'Template',
			renderCell: (cellValues) => {
				const apiRef = useGridApiContext();
				const rowType = cellValues.row.type?.toLowerCase();
				const row = cellValues.api?.getRow(cellValues.id);
				const { table_id: displayId, title, type: entityType } = row;
				const hasChildren = cellValues.rowNode.children?.length > 0;
				const hasParent = cellValues.rowNode.parent != null;
				const titleData = {
					displayId,
					title,
					entityType,
					hasParent,
					isParent: entityType == 'Submission Stage' || entityType == 'Evaluation Stage',
					hasChildren,
					row_id: row.id,
					expanded: cellValues.rowNode.childrenExpanded,
					isArchived: cellValues.row?.is_archived,
					isTemplate: true
				};
				const paddingLeft = (cellValues.row?.hierarchy?.length - 1) * 28;
				const tableStyle = { paddingLeft: paddingLeft };
				const handleExpand = (row_id, expanded) => {
					apiRef.current.setRowChildrenExpansion(row_id, !expanded);
				};
				const handleCardClick = () => {
					dispatch(setFormMetaInfo({ id: cellValues?.row.table_id }));
					if (rowType?.includes('submission stage')) {
						setSearchParams({
							...searchParams,
							stageCreation: 'application',
							stageId: cellValues.row.stage_id
						});
					} else if (rowType?.includes('evaluation stage')) {
						setSearchParams({
							...searchParams,
							stageCreation: 'evaluation',
							stageId: cellValues.row.stage_id
						});
					} else if (
						['application', 'review', 'application form', 'review form'].includes(rowType)
					) {
						dispatch(
							setFormMetaInfo({
								label: cellValues.label,
								second_label: cellValues.second_label,
								instructions: cellValues.instructions,
								description: cellValues.description,
								form_id: `${getEntityPrefix({ type: entityType, isBaseTemplate: true })}${
									cellValues.row.table_id
								}`
							})
						);
						setSearchParams({
							...searchParams,
							formBuilder: rowType?.includes('application') ? 'application' : 'review',
							formId: cellValues.row.form_id
						});
					} else {
						setSearchParams({
							...searchParams,
							stageCreation: 'process',
							processId: cellValues.row.process_id
						});
					}
				};

				return (
					<TableTitle
						titleData={titleData}
						style={tableStyle}
						handleCardClick={handleCardClick}
						handleExpand={handleExpand}
					/>
				);
			},
			flex: columnWidths.__tree_data_group__ || 1,
			minWidth: 400,
			maxWidth: 400
		},
		{
			field: 'type',
			headerName: 'Type',
			description: 'Template Type',
			renderCell: (cellValues) => {
				const isDisabled = cellValues?.row?.is_archived;

				return (
					<BasicCellText
						cellValues={cellValues}
						openTooltip={openTooltip}
						setOpenTooltip={setOpenTooltip}
						isDisabled={isDisabled}
					/>
				);
			},
			flex: 1
		},
		{
			field: 'description',
			headerName: 'Description',
			description: 'Description',
			renderCell: (cellValues) => {
				const isDisabled = cellValues?.row?.is_archived;

				return (
					<BasicCellText
						cellValues={cellValues}
						openTooltip={openTooltip}
						setOpenTooltip={setOpenTooltip}
						isDisabled={isDisabled}
					/>
				);
			},
			flex: columnWidths.description || 3,
			minWidth: 400
		},
		{
			field: 'published',
			headerName: 'Published',
			description: 'Published',
			type: 'date',
			renderCell: (cellValues) => {
				const isDisabled = cellValues?.row?.is_archived;

				return (
					<BasicCellText
						cellValues={cellValues}
						openTooltip={openTooltip}
						setOpenTooltip={setOpenTooltip}
						isDisabled={isDisabled}
						isDate={true}
					/>
				);
			},
			flex: columnWidths.published && 1
		},
		{
			field: 'updated',
			headerName: 'Updated',
			description: 'Updated',
			type: 'date',
			renderCell: (cellValues) => {
				const isDisabled = cellValues?.row?.is_archived;

				return (
					<BasicCellText
						cellValues={cellValues}
						openTooltip={openTooltip}
						setOpenTooltip={setOpenTooltip}
						isDisabled={isDisabled}
						isDate={true}
					/>
				);
			},
			flex: columnWidths.updated && 1
		},
		{
			field: 'linked_count',
			headerName: 'Linked Up',
			description: 'Linked Up',
			renderCell: (cellValues) => {
				const isDisabled = cellValues?.row?.is_archived;

				return (
					<BasicCellText
						cellValues={cellValues}
						openTooltip={openTooltip}
						setOpenTooltip={setOpenTooltip}
						emptyDefaultValue={true}
						isDisabled={isDisabled}
					/>
				);
			},
			flex: columnWidths.linked_count && 1,
			align: 'center',
			headerAlign: 'center'
		},
		{
			field: 'action',
			headerName: 'Actions',
			description: 'Actions',
			renderCell: (cellValues) => {
				const id = cellValues?.row?.form_id || cellValues?.row?.stage_id;
				const isArchived = cellValues?.row?.is_archived;
				const isLinked = cellValues?.row?.linked_count > 0;

				return (
					<div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
						{cellValues?.row?.type.includes('Stage') ? (
							<>
								<Tooltip arrow placement="top" title={'Copy'}>
									<IconButton aria-label="copy">
										<AddToPhotosIcon
											sx={{ fill: `${getOldBlueColor()}` }}
											onClick={() => handleCopy(cellValues?.row?.stage_id, 'stage')}
										/>
									</IconButton>
								</Tooltip>

								<Tooltip arrow placement="top" title={isArchived ? 'Unarchive' : 'Archive'}>
									<IconButton aria-label="archive">
										{isArchived ? (
											<UnarchiveIcon
												color={'primary'}
												onClick={() => handleArchiveUpdate(id, false)}
											/>
										) : (
											<ArchiveIcon
												color={'warning'}
												onClick={() => handleArchiveUpdate(id, true)}
											/>
										)}
									</IconButton>
								</Tooltip>

								<Tooltip
									arrow
									placement="top"
									title={isLinked ? 'Unlink all parents to enable' : 'Delete'}
								>
									<IconButton
										aria-label="delete"
										onClick={() => {
											!isLinked &&
												setSearchParams({
													...searchParams,
													deleteModal: id,
													type: cellValues?.row?.type?.includes('Stage') ? 'stage' : 'form'
												});
										}}
									>
										<DeleteForeverIcon
											color={cellValues?.row?.isLocked || isLinked ? 'action' : 'error'}
										/>
									</IconButton>
								</Tooltip>
								{!isArchived && (
									<ActionButton
										type={cellValues?.row?.type}
										clickedRow={cellValues?.row}
										allRows={cellValues?.api?.getRowModels()}
									/>
								)}
							</>
						) : (
							<>
								{cellValues?.row?.isDeletable || cellValues?.row?.is_adhoc ? (
									<Tooltip
										arrow
										placement="top"
										title={isArchived ? 'Unarchive parent to enable' : 'Delete'}
									>
										<IconButton
											aria-label="delete"
											onClick={() => {
												!isArchived &&
													setSearchParams({
														...searchParams,
														deleteModal: id,
														type: cellValues?.row?.type?.includes('Stage') ? 'stage' : 'form'
													});
											}}
										>
											<DeleteForeverIcon
												color={cellValues?.row?.isLocked || isArchived ? 'action' : 'error'}
											/>
										</IconButton>
									</Tooltip>
								) : (
									<Tooltip
										arrow
										placement="top"
										title={isArchived ? 'Unarchive parent to enable' : 'Detach'}
									>
										<IconButton
											aria-label="detach"
											disabled={cellValues?.row?.isLocked}
											onClick={() =>
												!isArchived &&
												handleUnlinking(cellValues?.row?.stage_id, cellValues?.row?.form_id, 'form')
											}
										>
											<LinkOffIcon color={isArchived ? 'action' : 'error'} />
										</IconButton>
									</Tooltip>
								)}
							</>
						)}
					</div>
				);
			},
			minWidth: 300
		}
	];
};

export default StageTemplateColumns;
